<template>
    <commercial-template>
        <div class="p-6 max-w-[1400px] mx-auto">

            <!-- Header -->
            <div class="flex items-center justify-between mb-6">
                <div>
                    <h1 class="text-2xl font-black text-gray-900 tracking-tight">Mes clients</h1>
                    <p class="text-sm text-gray-500 mt-0.5">Prospects convertis et validés</p>
                </div>
                <div class="flex items-center gap-3">
                    <span class="text-xs font-bold px-3 py-1.5 rounded-full bg-green-100 text-green-700">
                        <i class="fas fa-check-circle mr-1"></i> {{ pagination.total || 0 }} client(s)
                    </span>
                </div>
            </div>

            <!-- Search + filters bar -->
            <div class="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 mb-4 flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
                <div class="relative flex-1">
                    <i class="fas fa-search absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
                    <input
                        v-model="search"
                        type="text"
                        placeholder="Rechercher un client, un contact..."
                        class="w-full pl-9 pr-4 py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent transition bg-gray-50 placeholder-gray-400"/>
                </div>
                <button @click="fetchClients" class="flex items-center gap-2 px-4 py-2.5 bg-gray-100 text-gray-600 text-sm font-semibold rounded-xl hover:bg-gray-200 transition-all">
                    <i class="fas fa-sync-alt text-xs" :class="loading ? 'animate-spin' : ''"></i>
                    Actualiser
                </button>
            </div>

            <!-- Loader -->
            <div v-if="loading" class="bg-white rounded-2xl border border-gray-100 shadow-sm p-16 text-center">
                <div class="w-10 h-10 border-2 border-orange-500 border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
                <p class="text-sm text-gray-400 font-medium">Chargement des clients...</p>
            </div>

            <!-- Table -->
            <template v-else>
                <div class="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">

                    <!-- Empty state -->
                    <div v-if="filteredClients.length === 0" class="flex flex-col items-center justify-center py-20 px-6 text-center">
                        <div class="w-16 h-16 rounded-2xl bg-gray-100 flex items-center justify-center mb-4">
                            <i class="fas fa-building text-gray-300 text-2xl"></i>
                        </div>
                        <h3 class="text-base font-bold text-gray-600 mb-1">Aucun client</h3>
                        <p class="text-sm text-gray-400 max-w-xs">
                            <template v-if="search">Aucun résultat pour "{{ search }}".</template>
                            <template v-else>Validez un prospect depuis le kanban pour qu'il apparaisse ici.</template>
                        </p>
                        <a v-if="!search" href="/commercial/prospects" class="mt-4 text-sm font-bold text-orange-500 hover:text-orange-600 transition-colors">
                            Aller au kanban →
                        </a>
                    </div>

                    <template v-else>
                        <!-- Table header -->
                        <div class="grid grid-cols-12 gap-4 px-6 py-3 bg-gray-50/80 border-b border-gray-100 text-[10px] font-black text-gray-400 uppercase tracking-widest">
                            <div class="col-span-4">Société</div>
                            <div class="col-span-3 hidden md:block">Contact principal</div>
                            <div class="col-span-2 hidden lg:block">Effectif</div>
                            <div class="col-span-2 hidden md:block">Client depuis</div>
                            <div class="col-span-4 md:col-span-1 text-right">Actions</div>
                        </div>

                        <!-- Rows -->
                        <div
                            v-for="client in filteredClients"
                            :key="client.id"
                            class="group border-b border-gray-50 last:border-0 hover:bg-gray-50/50 transition-colors cursor-pointer"
                            @click="toggleExpand(client.id)">

                            <div class="grid grid-cols-12 gap-4 px-6 py-4 items-center">
                                <!-- Company -->
                                <div class="col-span-4 flex items-center gap-3 min-w-0">
                                    <div class="w-9 h-9 rounded-xl bg-gradient-to-br from-green-100 to-emerald-100 flex items-center justify-center font-black text-green-700 text-sm flex-shrink-0">
                                        {{ client.company_name.charAt(0).toUpperCase() }}
                                    </div>
                                    <div class="min-w-0">
                                        <p class="text-sm font-bold text-gray-900 truncate">{{ client.company_name }}</p>
                                        <p class="text-[10px] text-gray-400 mt-0.5">
                                            <i class="fas fa-users mr-1"></i>{{ (client.contacts || []).length }} contact(s)
                                        </p>
                                    </div>
                                </div>

                                <!-- Main contact -->
                                <div class="col-span-3 hidden md:block min-w-0">
                                    <template v-if="client.contacts && client.contacts.length">
                                        <p class="text-sm font-semibold text-gray-700 truncate">{{ client.contacts[0].prenom }} {{ client.contacts[0].nom }}</p>
                                        <p v-if="client.contacts[0].email" class="text-xs text-gray-400 truncate mt-0.5">{{ client.contacts[0].email }}</p>
                                    </template>
                                    <span v-else class="text-xs text-gray-300 italic">Aucun contact</span>
                                </div>

                                <!-- Effectif -->
                                <div class="col-span-2 hidden lg:block">
                                    <span v-if="client.effectif" class="text-xs font-bold px-2.5 py-1 rounded-full bg-gray-100 text-gray-600">
                                        {{ client.effectif }} emp.
                                    </span>
                                    <span v-else class="text-xs text-gray-300">—</span>
                                </div>

                                <!-- Validated at -->
                                <div class="col-span-2 hidden md:block">
                                    <span class="text-xs font-semibold text-green-600">
                                        <i class="fas fa-check-circle mr-1"></i>{{ client.validated_at | momentDate }}
                                    </span>
                                </div>

                                <!-- Actions -->
                                <div class="col-span-4 md:col-span-1 flex items-center justify-end gap-1.5">
                                    <i class="fas fa-chevron-down text-gray-300 text-xs transition-transform duration-200" :class="expandedId === client.id ? 'rotate-180' : ''"></i>
                                </div>
                            </div>

                            <!-- Expanded row -->
                            <div v-if="expandedId === client.id" class="px-6 pb-4 pt-0" @click.stop>
                                <div class="bg-gray-50 rounded-xl p-4 border border-gray-100">
                                    <div class="flex items-center justify-between mb-3">
                                        <span class="text-[10px] font-black text-gray-400 uppercase tracking-wide">Tous les contacts</span>
                                        <span class="text-[10px] font-black text-gray-400 uppercase tracking-wide">Validé le {{ client.validated_at | momentDate }}</span>
                                    </div>

                                    <div v-if="client.contacts && client.contacts.length" class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                                        <div
                                            v-for="contact in client.contacts"
                                            :key="contact.id"
                                            class="bg-white rounded-xl p-3 border border-gray-100">
                                            <div class="flex items-center gap-2 mb-2">
                                                <div class="w-7 h-7 rounded-lg bg-gradient-to-br from-green-100 to-emerald-100 flex items-center justify-center font-bold text-green-700 text-xs">
                                                    {{ ((contact.prenom || '?').charAt(0) + (contact.nom || '').charAt(0)).toUpperCase() }}
                                                </div>
                                                <div>
                                                    <p class="text-xs font-bold text-gray-800">{{ contact.prenom }} {{ contact.nom }}</p>
                                                    <p v-if="contact.poste" class="text-[10px] text-gray-400">{{ contact.poste }}</p>
                                                </div>
                                            </div>
                                            <div class="space-y-0.5">
                                                <p v-if="contact.email" class="text-[10px] text-gray-500 truncate">
                                                    <i class="fas fa-envelope mr-1 text-gray-300"></i>{{ contact.email }}
                                                </p>
                                                <p v-if="contact.telephone" class="text-[10px] text-gray-500">
                                                    <i class="fas fa-phone mr-1 text-gray-300"></i>{{ contact.telephone }}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                    <p v-else class="text-xs text-gray-400 italic">Aucun contact enregistré.</p>
                                </div>
                            </div>
                        </div>
                    </template>
                </div>

                <!-- Pagination -->
                <div v-if="pagination.last_page > 1" class="flex items-center justify-between mt-4 px-1">
                    <p class="text-xs text-gray-400">
                        Page <span class="font-bold text-gray-700">{{ pagination.current_page }}</span> sur <span class="font-bold text-gray-700">{{ pagination.last_page }}</span>
                        · <span class="font-bold text-gray-700">{{ pagination.total }}</span> client(s)
                    </p>
                    <div class="flex items-center gap-1.5">
                        <button
                            @click="goToPage(pagination.current_page - 1)"
                            :disabled="pagination.current_page === 1"
                            class="w-8 h-8 flex items-center justify-center rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all text-xs">
                            <i class="fas fa-chevron-left"></i>
                        </button>
                        <button
                            v-for="page in visiblePages"
                            :key="page"
                            @click="goToPage(page)"
                            class="w-8 h-8 flex items-center justify-center rounded-lg text-xs font-bold transition-all"
                            :class="page === pagination.current_page
                                ? 'bg-orange-500 text-white shadow-sm shadow-orange-200'
                                : 'border border-gray-200 text-gray-600 hover:bg-gray-50'">
                            {{ page }}
                        </button>
                        <button
                            @click="goToPage(pagination.current_page + 1)"
                            :disabled="pagination.current_page === pagination.last_page"
                            class="w-8 h-8 flex items-center justify-center rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all text-xs">
                            <i class="fas fa-chevron-right"></i>
                        </button>
                    </div>
                </div>
            </template>
        </div>
    </commercial-template>
</template>

<script>
import CommercialTemplate from './CommercialTemplate.vue';
import moment from 'moment';

export default {
    name: 'MesClients',
    components: { CommercialTemplate },
    data() {
        return {
            loading: true,
            clients: [],
            search: '',
            expandedId: null,
            pagination: {
                current_page: 1,
                last_page: 1,
                total: 0,
            },
        };
    },
    computed: {
        filteredClients() {
            if (!this.search.trim()) return this.clients;
            const q = this.search.toLowerCase();
            return this.clients.filter(c =>
                c.company_name.toLowerCase().includes(q) ||
                (c.contacts || []).some(ct =>
                    (ct.prenom + ' ' + ct.nom + ' ' + (ct.email || '')).toLowerCase().includes(q)
                )
            );
        },
        visiblePages() {
            const { current_page, last_page } = this.pagination;
            const pages = [];
            const delta = 2;
            for (let i = Math.max(1, current_page - delta); i <= Math.min(last_page, current_page + delta); i++) {
                pages.push(i);
            }
            return pages;
        }
    },
    filters: {
        momentDate(v) { return v ? moment(v).format('DD/MM/YYYY') : '—'; }
    },
    methods: {
        async fetchClients(page = 1) {
            this.loading = true;
            const res = await this.$request.get('/commercial/clients?page=' + page);
            if (res && res.clients) {
                this.clients = res.clients.data || res.clients;
                if (res.clients.current_page) {
                    this.pagination = {
                        current_page: res.clients.current_page,
                        last_page: res.clients.last_page,
                        total: res.clients.total,
                    };
                }
            }
            this.loading = false;
        },
        goToPage(page) {
            if (page < 1 || page > this.pagination.last_page) return;
            this.pagination.current_page = page;
            this.fetchClients(page);
        },
        toggleExpand(id) {
            this.expandedId = this.expandedId === id ? null : id;
        }
    },
    mounted() {
        this.fetchClients();
    }
};
</script>